import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { chartText } from '@/i18n/chartText';
import RequiredMark from '@/shein-platform/components/RequiredMark';
import { sheinPlatformAPI } from '@/utils/api';
import { SALE_PRICE_RISE_REASONS } from '@/shein-platform/utils/buildSheinPriceUpdate';

/**
 * 编辑改价确认：半/全托管选成本涨价原因；自运营选售价涨价原因。
 */
export default function PriceUpdateConfirmModal({
  open,
  mode = 'cost',
  shopId,
  changes,
  onCancel,
  onConfirm,
}) {
  const skcNames = useMemo(
    () => (Array.isArray(changes?.skcNames) ? changes.skcNames : []),
    [changes],
  );
  useTranslation('workbench');
  const [reasonBySkc, setReasonBySkc] = useState({});
  const [remarkBySkc, setRemarkBySkc] = useState({});
  const [saleRiseReason, setSaleRiseReason] = useState('1');
  const [reasonOptionsBySkc, setReasonOptionsBySkc] = useState({});
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setError('');
    setSubmitting(false);
    setSaleRiseReason('1');
    setRemarkBySkc({});
    const init = {};
    skcNames.forEach((name) => { init[name] = ''; });
    setReasonBySkc(init);

    if (mode !== 'cost' || !shopId || !skcNames.length) {
      setReasonOptionsBySkc({});
      return undefined;
    }

    let cancelled = false;
    setLoadingReasons(true);
    sheinPlatformAPI.queryChangePriceReasons(shopId, skcNames)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.list)
          ? res.list
          : (Array.isArray(res?.info?.list) ? res.info.list : []);
        const map = {};
        const defaults = {};
        list.forEach((row) => {
          const skc = String(row?.skcName || row?.skc_name || '').trim();
          if (!skc) return;
          const reasons = Array.isArray(row?.changeReasonList)
            ? row.changeReasonList
            : (Array.isArray(row?.change_reason_list) ? row.change_reason_list : []);
          map[skc] = reasons.map((r) => ({
            code: String(r?.reasonCode ?? r?.reason_code ?? ''),
            label: String(r?.reasonDesc || r?.reason_desc || r?.reasonCode || ''),
          })).filter((r) => r.code);
          if (map[skc][0]) defaults[skc] = map[skc][0].code;
        });
        skcNames.forEach((name) => {
          if (!map[name]) map[name] = [];
        });
        setReasonOptionsBySkc(map);
        setReasonBySkc((prev) => ({ ...defaults, ...prev, ...defaults }));
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || chartText('publishExtra.reasonsFail', '加载涨价原因失败'));
      })
      .finally(() => {
        if (!cancelled) setLoadingReasons(false);
      });
    return () => { cancelled = true; };
  }, [open, mode, shopId, skcNames.join('|')]);

  if (!open) return null;

  const title = mode === 'sale' ? chartText('publishExtra.confirmSale', '确认更新售价') : chartText('publishExtra.confirmCost', '确认更新成本价');
  const summary = (changes?.items || []).slice(0, 8);

  const handleConfirm = async () => {
    setError('');
    if (mode === 'cost') {
      const missing = skcNames.filter((name) => !String(reasonBySkc[name] || '').trim());
      if (missing.length) {
        setError(chartText('publishExtra.missingReasons', `请为以下 SKC 选择涨价原因：${missing.slice(0, 3).join('、')}`, { names: missing.slice(0, 3).join('、') }));
        return;
      }
    } else if (changes?.hasIncrease && !saleRiseReason) {
      setError(chartText('publishExtra.needReason', '涨价时请选择涨价原因'));
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm?.({
        reasonBySkc,
        remarkBySkc,
        saleRiseReason,
      });
    } catch (e) {
      setError(e?.message || chartText('publishExtra.submitFail', '提交失败'));
      setSubmitting(false);
    }
  };

  return (
    <div className="spo-batch-modal" role="dialog" aria-modal="true" aria-label={title} onClick={onCancel}>
      <div className="spo-batch-modal__panel" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="spo-batch-modal__head">
          <strong>{title}</strong>
          <button type="button" className="spo-batch-modal__close" aria-label={chartText('moduleShell.close', fixedUi("关闭"))} onClick={onCancel}>×</button>
        </div>
        <div className="spo-batch-modal__body">
          <p className="spo-batch-modal__hint">
            {mode === 'sale'
              ? chartText('publishExtra.saleHintShop', '自运营售价须走专用改价接口；不传特价时平台会将特价置 0。涨价可能进入审核，待审期间请勿重复改价。')
              : chartText('publishExtra.costHint', '半托管/全托管成本价须走 update-cost；涨价原因必填。参与营销活动锁定涨价的 SKC 将无法提交。')}
          </p>
          {summary.length ? (
            <ul className="spo-batch-modal__meta" style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
              {summary.map((row) => (
                <li key={row.key}>
                  {row.colorLabel || row.skcName}
                  {row.sizeLabel ? ` / ${row.sizeLabel}` : ''}
                  {': '}
                  {row.prevPrice != null ? `${row.prevPrice} → ` : ''}
                  <strong>{row.price}</strong>
                  {row.currency ? ` ${row.currency}` : ''}
                  {row.increased ? chartText('publishExtra.increased', '（涨价）') : chartText('publishExtra.decreased', '（降价/调整）')}
                </li>
              ))}
              {(changes.items || []).length > summary.length ? (
                <li>{chartText('publishExtra.skuMore', `…共 ${changes.items.length} 个 SKU`, { count: changes.items.length })}</li>
              ) : null}
            </ul>
          ) : null}

          {mode === 'sale' ? (
            <div className="spo-field">
              <span className="spo-field__label">{chartText('publishExtra.riseReason', fixedUi("涨价原因"))}{changes?.hasIncrease ? <RequiredMark /> : null}</span>
              <select
                className="spo-input"
                value={saleRiseReason}
                disabled={submitting}
                onChange={(e) => setSaleRiseReason(e.target.value)}
              >
                {SALE_PRICE_RISE_REASONS.map((opt) => (
                  <option key={opt.code} value={opt.code}>{opt.label}</option>
                ))}
              </select>
            </div>
          ) : (
            skcNames.map((skcName) => {
              const options = reasonOptionsBySkc[skcName] || [];
              return (
                <div key={skcName} className="spo-field" style={{ marginBottom: 12 }}>
                  <span className="spo-field__label">{chartText('publishExtra.riseFor', `SKC ${skcName} 涨价原因`, { name: skcName })} <RequiredMark /></span>
                  <select
                    className="spo-input"
                    value={reasonBySkc[skcName] || ''}
                    disabled={submitting || loadingReasons}
                    onChange={(e) => setReasonBySkc((prev) => ({ ...prev, [skcName]: e.target.value }))}
                  >
                    <option value="">{loadingReasons ? chartText('tiktokPublish.loading', fixedUi("加载中…")) : chartText('shop.pleaseSelect', fixedUi("请选择"))}</option>
                    {options.map((opt) => (
                      <option key={opt.code} value={opt.code}>{opt.label || opt.code}</option>
                    ))}
                  </select>
                  <textarea
                    className="spo-input spo-batch-modal__textarea"
                    style={{ marginTop: 8 }}
                    rows={2}
                    placeholder={chartText('publishExtra.remarkPh', '涨价说明（可选，最多 1000 字）')}
                    value={remarkBySkc[skcName] || ''}
                    disabled={submitting}
                    onChange={(e) => setRemarkBySkc((prev) => ({ ...prev, [skcName]: e.target.value }))}
                  />
                </div>
              );
            })
          )}

          {error ? <p className="spo-field__error" role="alert">{error}</p> : null}
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" disabled={submitting} onClick={onCancel}>
            {chartText('shop.cancel', '取消')}
          </button>
          <button
            type="button"
            className="spo-btn spo-btn--primary spo-btn--sm"
            disabled={submitting || loadingReasons}
            onClick={handleConfirm}
          >
            {submitting ? chartText('returnList.submitting', fixedUi("提交中…")) : chartText('publishExtra.confirmSubmit', '确认改价并提交')}
          </button>
        </div>
      </div>
    </div>
  );
}
